import React from 'react';
import { ChevronDown, UserRound } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useDemoProfile } from '@/state/profile';
import { cn } from '@/lib/utils';

/** Initialer til avatar-sirkel. */
export function initialsOf(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Bytter hvilken fiktiv medarbeider «Min dag» gjelder for. Alltid merket «Demoprofil – ikke innlogging»:
 * previewen har ingen ekte pålogging eller roller, og dette later ikke som noe annet.
 */
export function DemoProfileSwitcher({ className, compact = false }) {
  const { employee, employees, setEmployeeId, loading } = useDemoProfile();
  return (
    <div className={cn('flex min-w-0 items-center gap-2.5', className)} data-testid="demo-profile">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--hr-navy)] text-[13px] font-semibold text-white" aria-hidden="true">
        {employee ? initialsOf(employee.name) : <UserRound className="h-4 w-4" />}
      </span>
      <div className="min-w-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={!employees.length}
              className="inline-flex max-w-full items-center gap-1 rounded-sm text-left text-[15px] font-semibold text-[var(--hr-heading)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] disabled:opacity-60"
              aria-label="Bytt demoprofil"
              data-testid="demo-profile-switcher-trigger"
            >
              <span className="truncate">{employee?.name ?? (loading ? 'Henter medarbeidere …' : 'Velg demoprofil')}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[280px] rounded-[12px]" data-testid="demo-profile-menu">
            <DropdownMenuLabel className="text-[12px] font-normal text-[var(--hr-muted)]" data-testid="demo-profile-synthetic-note">
              Fiktive medarbeidere – bytt for å se Min dag for en annen. Ikke innlogging.
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup value={employee?.id ?? ''} onValueChange={setEmployeeId}>
              {employees.map((e) => (
                <DropdownMenuRadioItem key={e.id} value={e.id} className="rounded-[8px] py-2 text-[14px]" data-testid={`demo-profile-option-${e.id}`}>
                  <span className="truncate">
                    {e.name} <span className="text-[var(--hr-muted-2)]">· {e.role}</span>
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        {!compact && (
          <div className="truncate text-[13px] text-[var(--hr-muted)]" data-testid="demo-profile-note">
            {employee?.role ? `${employee.role} · ` : ''}Demoprofil – ikke innlogging
          </div>
        )}
      </div>
    </div>
  );
}
